'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { trackDiscoveryCallClick } from '@/lib/analytics';
import { getStartNowRoute, openCalendly } from '@/lib/ctaRouting';
import { getUserContext } from '@/lib/userContext';

/**
 * HERO SECTION - EXACT LIVE SITE MATCH (purplecowunlimited.com)
 * ============================================================
 * CRITICAL REQUIREMENTS:
 * 
 * 1. Header tight 64px bar with 1328px container
 * 2. Hero high positioning - headline sits close to header
 * 3. Cow illustration aligns with headline top, not pushed down
 * 4. Headline: Bebas Neue Regular, #ffefee color, tight tracking
 * 5. Cards: NO icons, simple text, correct colors (#240c33, #8924B3)
 * 6. Cards: NO cyan borders, NO glow effects
 * 7. Cards visible at 1440x900 without scrolling
 * 
 * SPACING UPDATE:
 * - Desktop top padding increased by 40px
 * - Left column spacing: headline-to-subhead 32px, subhead-to-paragraph 32px, paragraph-to-buttons 36px
 * - Increased internal spacing for more relaxed feel matching live site
 */

export default function HeroSection() {
  const router = useRouter();

  const handleStartNow = async () => {
    const route = await getStartNowRoute();
    router?.push(route);
  };

  const handleBookCall = () => {
    // Track the click before opening
    getUserContext()?.then(context => {
      const segment = context?.role || 'unauthenticated';
      trackDiscoveryCallClick(
        segment === 'super_admin' ? 'super_admin' : 
        segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
        segment === 'team' ? 'team' : 
        segment === 'client' ? 'client' : 'unauthenticated',
        '/',
        'hero'
      );
    });
    openCalendly();
  };

  return (
    <section className="relative bg-[#2a0f3a] text-white overflow-hidden">
      {/* Background image overlay */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'url("/images/img_image.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Hero content container - 1328px max-width, increased desktop top padding by 40px */}
      <div 
        className="relative mx-auto px-6 lg:px-12 pt-12 md:pt-16"
        style={{
          maxWidth: '1328px',
          paddingTop: '60px', // Desktop: increased by 40px (20px base + 40px = 60px)
          paddingBottom: '28px'
        }}
      >
        
        {/* Two Column Grid - Left text + Right cow illustration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          
          {/* Left Column - Headline and copy, left aligned, max-width 520px, additional top spacing */}
          <div className="flex flex-col items-start text-left z-10" style={{ maxWidth: '520px', paddingTop: '48px' }}>
            
            {/* Headline - Bebas Neue Regular, #ffefee, tight tracking */}
            <h1 
              className="font-bebas uppercase"
              style={{ 
                fontWeight: 400,
                fontSize: '120px',
                lineHeight: '1.05',
                letterSpacing: '-0.5px',
                color: '#ffefee'
              }}
            >
              <span className="block text-[120px] md:text-[100px] sm:text-[72px]" style={{ lineHeight: '1.05' }}>PURPLE COW</span>
              <span className="block text-[120px] md:text-[100px] sm:text-[72px]" style={{ lineHeight: '1.05' }}>UNLIMITED</span>
            </h1>
            
            {/* Subheadline - Poppins, 32px spacing from headline */}
            <p 
              className="font-poppins max-w-[520px]"
              style={{ 
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '1.35',
                color: 'rgba(255,255,255,0.9)',
                marginTop: '32px'
              }}
            >
              Remarkable creative and marketing support for one flat monthly fee.
            </p>

            {/* Body - Poppins, 32px spacing from subheadline */}
            <p 
              className="font-poppins max-w-[520px]"
              style={{ 
                fontSize: '14px',
                lineHeight: '1.65',
                color: 'rgba(255,255,255,0.8)',
                marginTop: '32px'
              }}
            >
              You are leading a business, a team, or a department and you are expected to deliver results without burning out your people or your budget.
            </p>

            {/* CTA Buttons - 36px spacing from paragraph */}
            <div className="flex flex-col sm:flex-row" style={{ marginTop: '36px', gap: '14px' }}>
              <button
                onClick={handleStartNow}
                className="font-poppins text-white transition-all duration-300 hover:bg-[#7a1f9e]"
                style={{ 
                  backgroundColor: '#a035d1',
                  borderRadius: '9999px',
                  height: '48px',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Start Now
              </button>
              
              <button
                onClick={handleBookCall}
                className="font-poppins text-white bg-transparent transition-all duration-300"
                style={{
                  border: '2px solid white',
                  borderRadius: '9999px',
                  height: '48px',
                  paddingLeft: '36px',
                  paddingRight: '36px',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Book a Free Strategy Call
              </button>
            </div>
          </div>

          {/* Right Column - Cow illustration aligned to headline top */}
          <div className="relative z-10 flex items-start justify-center lg:justify-end">
            <div className="relative w-full max-w-[520px]">
              <Image 
                src="/hero-m.png" 
                alt="Purple Cow mascot with laptop representing unlimited creative services"
                width={520}
                height={520}
                priority
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Three Cards - NO icons, simple text, flat rounded corners, closer spacing */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6" style={{ marginTop: '48px' }}>
          
          {/* THE PROBLEM card - #240c33 background, NO icons, simple text */}
          <div 
            className="transition-all"
            style={{ 
              backgroundColor: '#240c33',
              borderRadius: '16px',
              padding: '28px'
            }}
          >
            <h3 
              className="text-white font-bebas uppercase mb-4"
              style={{
                fontSize: '28px',
                fontWeight: 400,
                letterSpacing: '-0.5px'
              }}
            >
              THE PROBLEM
            </h3>
            <ul className="space-y-3">
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Design is behind
              </li>
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Video requests pile up
              </li>
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Website needs updates
              </li>
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Social media needs consistency
              </li>
            </ul>
          </div>

          {/* OLD SOLUTIONS DON'T WORK card - #240c33 background, NO icons, simple text */}
          <div 
            className="transition-all"
            style={{ 
              backgroundColor: '#240c33',
              borderRadius: '16px',
              padding: '28px'
            }}
          >
            <h3 
              className="text-white font-bebas uppercase mb-4"
              style={{
                fontSize: '28px',
                fontWeight: 400,
                letterSpacing: '-0.5px'
              }}
            >
              OLD SOLUTIONS DON'T WORK
            </h3>
            <ul className="space-y-3">
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Hiring full time creatives feels heavy
              </li>
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Freelancers disappear
              </li>
              <li className="font-poppins text-[15px] text-gray-200" style={{ lineHeight: '1.6' }}>
                Agencies surprise you with new invoices
              </li>
            </ul>
          </div>

          {/* THE SOLUTION card - #8924B3 background, NO cyan border, NO glow, simple text */}
          <div 
            className="transition-all"
            style={{ 
              backgroundColor: '#8924B3',
              borderRadius: '16px',
              padding: '28px'
            }}
          >
            <h3 
              className="text-white font-bebas uppercase mb-4"
              style={{
                fontSize: '28px',
                fontWeight: 400,
                letterSpacing: '-0.5px'
              }}
            >
              THE SOLUTION
            </h3>
            <p className="font-poppins text-[15px] text-white mb-4" style={{ lineHeight: '1.6' }}>
              Purple Cow Unlimited gives you a full creative and marketing team without the headaches.
            </p>
            <ul className="space-y-3">
              <li className="font-poppins text-[15px] text-white" style={{ lineHeight: '1.6' }}>
                One subscription
              </li>
              <li className="font-poppins text-[15px] text-white" style={{ lineHeight: '1.6' }}>
                One remarkable Purple Cow herd
              </li>
              <li className="font-poppins text-[15px] text-white" style={{ lineHeight: '1.6' }}>
                Unlimited requests
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}